import { observable, computed, action, makeObservable } from 'mobx';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { ManagerListTypeSummaryLookup } from 'nomst/Stores/Decision/ManagerListSidePanel/managerListTypeSummaryLookup';
import { SidePanelListType, WorkflowStatusListType, WorkflowStatus } from 'typings/enums';
import { ManagerCardsContainer } from 'nomst/Stores/Decision/ManagerListSidePanel/managerCardsContainer';
import { dataManagerFactory } from 'utils/MobxDataManager/singletons';
import { workflowStatusFromListTypeMap } from 'nomst/Stores/Decision/Constants/workflowStatusMap';
import { ManagerWorkflowStore } from 'nomst/Stores/Decision/managerWorkflow.store';
import { User } from 'nomst/Stores/User/user';

export class ManagerListSidePanelStore {
    private _configuration: Configuration;

    private _managerCardsContainer: ManagerCardsContainer;

    private readonly _managerWorkflowStore: ManagerWorkflowStore;

    private readonly _user: User;

    @observable private _sidePanelListType: SidePanelListType = SidePanelListType.WorkflowStatus;

    @observable private _workflowStatusType: WorkflowStatusListType = WorkflowStatusListType.Open;

    @observable private _budgetCycleId: Nullable<number>;

    @observable private _managerWorkflowSummaryLookups: ManagerListTypeSummaryLookup[];

    @observable private _managerOverBudgetSummaryLookups: ManagerListTypeSummaryLookup[];

    @observable private _selectedManagerIds: Map<string, string>;

    @observable private _sidePanelIsOpen: boolean = true;

    @observable private _selectAllIsChecked: boolean = false;

    @observable private _assignedToMeOnly: boolean = false;

    constructor(configuration: Configuration, managerWorkflowStore: ManagerWorkflowStore, user: User) {
        makeObservable(this);
        this._configuration = configuration;
        this._managerWorkflowStore = managerWorkflowStore;
        this._user = user;
        this._managerCardsContainer = new ManagerCardsContainer(configuration.masterAccountId, configuration.cycleId);

        this._selectedManagerIds = new Map<string, string>();
    }

    @computed
    get sidePanelListType(): SidePanelListType {
        return this._sidePanelListType;
    }

    @computed
    get workflowStatusType(): WorkflowStatusListType {
        return this._workflowStatusType;
    }

    @computed
    get budgetCycleId(): Nullable<number> {
        return this._budgetCycleId;
    }

    @computed
    get sidePanelIsOpen(): boolean {
        return this._sidePanelIsOpen;
    }

    @computed
    get managerWorkflowSummaryLookups(): ManagerListTypeSummaryLookup[] {
        return this._managerWorkflowSummaryLookups;
    }

    @computed
    get managerOverBudgetSummaryLookups(): ManagerListTypeSummaryLookup[] {
        return this._managerOverBudgetSummaryLookups;
    }

    @computed
    get selectedManagerIds(): string[] {
        return Array.from(this._selectedManagerIds.values());
    }

    @computed
    get allManagerIds(): string[] {
        const workflowStatus = workflowStatusFromListTypeMap.get(this._workflowStatusType) || WorkflowStatus.Open;
        const budgetCycleId = this._budgetCycleId || 0;
        const managerCards: IManagerCard[] = this._sidePanelListType === SidePanelListType.WorkflowStatus
            ? this._managerCardsContainer.getWorkflowStatusManagers(workflowStatus)
            : this._managerCardsContainer.getOverBudgetManagers(budgetCycleId);

        const managerSourceIds = managerCards.map(managerCard => managerCard.managerSourceId);
        return this._assignedToMeOnly ? this.getFilteredManagerIds(managerSourceIds) : managerSourceIds;
    }

    @computed
    get selectAllIsChecked(): boolean {
        return this._selectAllIsChecked;
    }

    @computed
    get assignedToMeOnly(): boolean {
        return this._assignedToMeOnly;
    }

    get managerCardsContainer(): ManagerCardsContainer {
        return this._managerCardsContainer;
    }

    getFilteredManagerIds(managerSourceIds: string[]): string[] {
        const managerWorkflows = this._managerWorkflowStore.managerWorkflows;
        return managerSourceIds.filter(id => {
            const workflow = managerWorkflows.get(id);
            if (!workflow || !workflow.currentReviewer) {
                return false;
            }

            const userAsReviewer = this._managerWorkflowStore.getCurrentUserAsReviewer(workflow, this._user);
            return userAsReviewer && workflow.currentReviewer.reviewerId === userAsReviewer.reviewerId;
        });
    }

    isManagerSelected(managerSourceId: string): boolean {
        return this._selectedManagerIds.has(managerSourceId);
    }

    @action
    clearSelectedManagerIds(): void {
        this._selectedManagerIds.clear();
    }

    @action
    setToggleSidePanel(sidePanelIsOpen: boolean): void {
        if (this.isFirstLevelManager) {
            this._sidePanelIsOpen = false;
            return;
        }
        this._sidePanelIsOpen = sidePanelIsOpen;
    }

    @action
    setSelectedManagerIdsMap(managerSourceIds: string[]): void {
        this._selectedManagerIds.clear();
        if (!managerSourceIds || !managerSourceIds[0]) {
            return;
        }

        managerSourceIds.forEach(id => this._selectedManagerIds.set(id, id));

        if (this.allManagerIds.length === managerSourceIds.length && managerSourceIds.length > 1) {
            this._selectAllIsChecked = true;
        }
    }

    @action
    setSidePanelListType(sidePanelListType: SidePanelListType): void {
        this._sidePanelListType = sidePanelListType;
    }

    @action
    setBudgetCycleId(budgetCycleId: number): void {
        this._budgetCycleId = budgetCycleId;
    }

    @action
    setWorkflowStatusType(workflowStatusType: WorkflowStatusListType): void {
        this._workflowStatusType = workflowStatusType;
    }

    @action
    setManagerOverBudgetSummaryLookups(lookups: ManagerListTypeSummaryLookup[]): void {
        this._managerOverBudgetSummaryLookups = lookups;
    }

    @action
    setManagerWorkflowSummaryLookups(lookups: ManagerListTypeSummaryLookup[]): void {
        this._managerWorkflowSummaryLookups = lookups;
    }

    @action
    toggleSelectAllManagers(selectAll: boolean): void {
        this._selectAllIsChecked = selectAll;
        if (!selectAll) {
            this.setSelectedManagerIdsMap([this.allManagerIds[0]]);
            return;
        }

        this.setSelectedManagerIdsMap(this.allManagerIds);
    }

    @action
    toggleAssignedToMe(assignedToMeOnly: boolean): void {
        this._assignedToMeOnly = assignedToMeOnly;
        if (assignedToMeOnly) {
            const selectedAssignedToMe = this.getFilteredManagerIds(this.selectedManagerIds);
            if (selectedAssignedToMe.length > 0) {
                this.setSelectedManagerIdsMap(selectedAssignedToMe);
                return;
            }
            this.setSelectedManagerIdsMap([this.allManagerIds[0]]);
        } else {
            const managerSourceIds = this._selectAllIsChecked ? this.allManagerIds : this.selectedManagerIds;
            this.setSelectedManagerIdsMap(managerSourceIds);
        }
    }

    @action
    toggleManagerSelection(managerSourceId: string): void {
        if (this._selectedManagerIds.delete(managerSourceId)) {
            this._selectAllIsChecked = false;
            return;
        }

        this._selectedManagerIds.set(managerSourceId, managerSourceId);
    }

    get isFirstLevelManager(): boolean {
        return this._user.isFirstLevelManager;
    }

    get isMissingManagerRole(): boolean {
        return this._user.missingManagerRole;
    }

    get hasNoRoles(): boolean {
        return this._user.hasNoRolesAssigned;
    }

    get userEmployeeId(): string {
        return this._user.employeeSourceId ?? '';
    }

    @action
    async fetchOverBudgetManagerIds(budgetCycleId: number): Promise<string[]> {
        await this._managerCardsContainer.fetchManagerCards(
            SidePanelListType.OverBudget,
            budgetCycleId.toString());
        const managers = this._managerCardsContainer.getOverBudgetManagers(budgetCycleId);
        return this.getAllOrFirstManagerId(managers);
    }

    @action
    async fetchWorkflowManagerIds(workflowStatus: WorkflowStatusListType): Promise<string[]> {
        await this._managerCardsContainer.fetchManagerCards(
            SidePanelListType.WorkflowStatus,
            workflowStatus);
        const status = workflowStatusFromListTypeMap.get(workflowStatus);
        if (status) {
            const managers = this._managerCardsContainer.getWorkflowStatusManagers(status);
            return this.getAllOrFirstManagerId(managers);
        }

        return [];
    }

    async fetchAllWorkflows(): Promise<void> {
        await Promise.all([
            this._managerCardsContainer.fetchManagerCards(
                SidePanelListType.WorkflowStatus,
                WorkflowStatusListType.Open),
            this._managerCardsContainer.fetchManagerCards(
                SidePanelListType.WorkflowStatus,
                WorkflowStatusListType.InProgress),
            this._managerCardsContainer.fetchManagerCards(
                SidePanelListType.WorkflowStatus,
                WorkflowStatusListType.Pending),
            this._managerCardsContainer.fetchManagerCards(
                SidePanelListType.WorkflowStatus,
                WorkflowStatusListType.Approved)
        ]);
    }

    @action
    async setWorkflowManagerList(
        sidePanelListType: SidePanelListType,
        workflowStatusType: WorkflowStatusListType): Promise<void> {

        await this.setUpListTypeData(sidePanelListType);

        this._workflowStatusType = workflowStatusType;
        await this._managerCardsContainer.fetchManagerCards(
            this._sidePanelListType,
            this._workflowStatusType.toString());
    }

    @action
    async setOverBudgetManagerList(
        sidePanelListType: SidePanelListType,
        budgetCycleId: number): Promise<void> {

        await this.setUpListTypeData(sidePanelListType);

        this._budgetCycleId = budgetCycleId;

        await this._managerCardsContainer.fetchManagerCards(
            this._sidePanelListType,
            this._budgetCycleId.toString());
    }

    @action
    clearManagerList(): void {
        this._sidePanelListType = SidePanelListType.WorkflowStatus;
        this._workflowStatusType = WorkflowStatusListType.Open;
        this._budgetCycleId = null;
        this._selectAllIsChecked = false;
        this._managerOverBudgetSummaryLookups = [];
        this._managerWorkflowSummaryLookups = [];
        this._managerCardsContainer.clear();
        this.clearSelectedManagerIds();
    }

    @action
    async resetManagerList(): Promise<void> {
        this._managerOverBudgetSummaryLookups = [];
        this._managerWorkflowSummaryLookups = [];
        this.clearSelectedManagerIds();

        let selectedManagers = [];
        let listType = SidePanelListType.WorkflowStatus;
        if (this._budgetCycleId) {
            listType = SidePanelListType.OverBudget;
            selectedManagers = await this.fetchOverBudgetManagerIds(this._budgetCycleId);
        } else {
            selectedManagers = await this.fetchWorkflowManagerIds(this._workflowStatusType);
        }

        this.setSelectedManagerIdsMap(selectedManagers);
        await this.setUpListTypeData(listType);
        this._managerCardsContainer.clear();
    }

    @action
    resetBudgetCycleId(): void {
        this._budgetCycleId = null;
    }

    @action
    updateConfiguration(config: Configuration): void {
        this._configuration = config;
        if (this._managerCardsContainer.currentCycleId !== config.cycleId) {
            this._managerCardsContainer = new ManagerCardsContainer(config.masterAccountId, config.cycleId);
        }
    }

    @action
    async getManagerWorkflowSummaries(): Promise<void> {
        const managerListTypeSummaryLookupsDtos = (await dataManagerFactory
            .getManagerDataManager()
            .getManagerListTypeSummaryLookups(
                this._configuration.masterAccountId,
                this._configuration.cycleId,
                SidePanelListType.WorkflowStatus)) || [];
        this._managerWorkflowSummaryLookups = managerListTypeSummaryLookupsDtos
            .map(entry => new ManagerListTypeSummaryLookup(entry));
    }

    @action
    async getManagerOverBudgetSummaries(): Promise<void> {
        const managerListTypeSummaryLookupsDtos = (await dataManagerFactory
            .getManagerDataManager()
            .getManagerListTypeSummaryLookups(
                this._configuration.masterAccountId,
                this._configuration.cycleId,
                SidePanelListType.OverBudget)) || [];
        this._managerOverBudgetSummaryLookups = managerListTypeSummaryLookupsDtos
            .map(entry => new ManagerListTypeSummaryLookup(entry));

    }

    private async setUpListTypeData(sidePanelListType: SidePanelListType): Promise<void> {
        if (!sidePanelListType) {
            this.setToggleSidePanel(false);
            this.clearManagerList();
            return;
        }

        if (sidePanelListType !== this._sidePanelListType) {
            this._selectAllIsChecked = false;
        }

        this._sidePanelListType = sidePanelListType;
        if (this._sidePanelListType === SidePanelListType.WorkflowStatus) {
            await this.getManagerWorkflowSummaries();
        } else {
            await this.getManagerOverBudgetSummaries();
        }
    }

    private getAllOrFirstManagerId(managers: IManagerCard[]): string[] {
        if (!managers.length) {
            return [];
        }

        let managerSourceIds = managers.map(mgr => mgr.managerSourceId);
        if (this._assignedToMeOnly) {
            managerSourceIds = this.getFilteredManagerIds(managerSourceIds);
        }

        return this._selectAllIsChecked
            ? managerSourceIds
            : [managerSourceIds[0]];
    }
}